import { keyframes } from '@emotion/react';

import { createSelectCollection } from 'ui/Select';

import {
  CanvasSizeEnum,
  ComparisonLineValueTypeEnum,
  CountryEnum,
  DownloadImageFormatEnum,
  DownloadScalePngEnum,
  LabelPosEnum,
  NrOfDecimalsEnum,
  ReportingPeriodEnum,
  SecondLabelLineValueTypeEnum,
  StatementTypeEnum,
} from './generated-api';

// Save Sankey state to the server every second.
export const AUTOSAVE_DEBOUNCE_MS = 1000;

// Poll the server for the status of async tasks every 2 seconds.
export const ASYNC_TASK_POLL_INTERVAL_MS = 2000;

// Refresh the Sankey diagram every 300ms.
export const DIAGRAM_REFRESH_DEBOUNCE_MS = 300;

// Apply delayed input to form every 200ms.
export const DELAYED_INPUT_DEBOUNCE_MS = 200;

// Delay applied to storing new states in the Undo & Redo history.
export const STATE_HISTORY_DEBOUNCE_MS = 500;

// Maximum number of undos user can do when editing the Sankey diagram.
export const MAX_UNDOS = 100;

export const supportedB2BCountryOptions: SelectOption<CountryEnum>[] = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'AT', label: 'Austria' },
  { value: 'BE', label: 'Belgium' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'CA', label: 'Canada' },
  { value: 'HR', label: 'Croatia' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'DK', label: 'Denmark' },
  { value: 'EE', label: 'Estonia' },
  { value: 'FI', label: 'Finland' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'GR', label: 'Greece' },
  { value: 'HU', label: 'Hungary' },
  { value: 'IE', label: 'Ireland' },
  { value: 'IT', label: 'Italy' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'NO', label: 'Norway' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'RO', label: 'Romania' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'ES', label: 'Spain' },
  { value: 'SE', label: 'Sweden' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'ROW', label: 'Rest of the world' },
];

export const supportedB2BCountriesCollection = createSelectCollection(
  supportedB2BCountryOptions
);

export const supportedB2CCompanyCountries: CountryEnum[] = [
  'US',
  'AU',
  'CA',
  'NZ',
  'ZA',
];

export const reportingPeriodOptions: SelectOption<ReportingPeriodEnum>[] = [
  { value: 'january', label: 'January' },
  { value: 'february', label: 'February' },
  { value: 'march', label: 'March' },
  { value: 'april', label: 'April' },
  { value: 'may', label: 'May' },
  { value: 'june', label: 'June' },
  { value: 'july', label: 'July' },
  { value: 'august', label: 'August' },
  { value: 'september', label: 'September' },
  { value: 'october', label: 'October' },
  { value: 'november', label: 'November' },
  { value: 'december', label: 'December' },
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Q4', label: 'Q4' },
  { value: 'H1', label: 'H1' },
  { value: 'H2', label: 'H2' },
  { value: 'full_year', label: 'Full Year' },
];

export const statementTypeOptions: SelectOption<StatementTypeEnum>[] = [
  { value: 'income', label: 'Income statement' },
  { value: 'cashflow', label: 'Cash flow statement' },
  { value: 'balance', label: 'Balance sheet' },
  { value: 'budget', label: 'Budget' },
  { value: 'other', label: 'Other' },
];

export const labelPositionsOptions: SelectOption<LabelPosEnum>[] = [
  { value: 'before', label: 'Before the node' },
  { value: 'after', label: 'After the node' },
  { value: 'outside', label: 'Outside the node' },
  { value: 'inside', label: 'Inside the node' },
];

export const numberOfDecimalsOptions: SelectOption<NrOfDecimalsEnum>[] = [
  { value: 'all', label: 'All' },
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
];

export const downloadImageFormatOptions: SelectOption<DownloadImageFormatEnum>[] =
  [
    { value: 'png', label: 'PNG' },
    { value: 'svg', label: 'SVG' },
  ];

export const downloadScalePNGOptions: SelectOption<DownloadScalePngEnum>[] = [
  { value: '1', label: 'Low (original size)' },
  { value: '2', label: 'Medium (2x size)' },
  { value: '4', label: 'High (4x size)' },
];

export const canvasSizeOptions: SelectOption<CanvasSizeEnum>[] = [
  { value: '1', label: 'Small (352×300px)' },
  { value: '2', label: 'Medium (800×480px)' },
  { value: '3', label: 'Large (960×600px)' },
  { value: 'custom', label: 'Custom' },
];

export const secondLabelLineValueOptions: SelectOption<SecondLabelLineValueTypeEnum>[] =
  [
    { value: 'amount', label: 'Absolute' },
    { value: 'percent-neighbor', label: '% of neighbor' },
    { value: 'percent-highest', label: '% of highest amount' },
  ];

export const thirdLabelLineValueOptions: SelectOption<ComparisonLineValueTypeEnum>[] =
  [
    { value: 'percent', label: 'Percentage' },
    { value: 'absolute', label: 'Absolute' },
  ];

// For the listed reporting periods, we'll assume we're dealing with a fiscal year,
// otherwise we just treat the year as the regular year.
export const reportingPeriodsImplyingFiscalYear: ReportingPeriodEnum[] = [
  ReportingPeriodEnum.FULL_YEAR,
  ReportingPeriodEnum.Q1,
  ReportingPeriodEnum.Q2,
  ReportingPeriodEnum.Q3,
  ReportingPeriodEnum.Q4,
  ReportingPeriodEnum.H1,
  ReportingPeriodEnum.H2,
];

export const fadeInAnimation = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// From https://pqina.nl/blog/css-spring-animation-with-linear-easing-function/ (stiffness = 0.3, damping = 0.3)
export const springAnimation = keyframes`
  0.00% { transform: scale(0.00) }
  2.63% { transform: scale(-0.0202) }
  5.26% { transform: scale(0.269) }
  7.89% { transform: scale(0.550) }
  10.5% { transform: scale(0.780) }
  13.2% { transform: scale(1.13) }
  15.8% { transform: scale(1.33) }
  18.4% { transform: scale(1.37) }
  21.1% { transform: scale(1.29) }
  23.7% { transform: scale(1.15) }
  26.3% { transform: scale(1.00) }
  28.9% { transform: scale(0.899) }
  31.6% { transform: scale(0.855) }
  34.2% { transform: scale(0.869) }
  36.8% { transform: scale(0.919) }
  39.5% { transform: scale(0.979) }
  42.1% { transform: scale(1.03) }
  44.7% { transform: scale(1.05) }
  47.4% { transform: scale(1.05) }
  50.0% { transform: scale(1.04) }
  52.6% { transform: scale(1.02) }
  55.3% { transform: scale(0.996) }
  57.9% { transform: scale(0.983) }
  60.5% { transform: scale(0.978) }
  63.2% { transform: scale(0.982) }
  65.8% { transform: scale(0.989) }
  68.4% { transform: scale(0.998) }
  71.1% { transform: scale(1.00) }
  73.7% { transform: scale(1.01) }
  76.3% { transform: scale(1.01) }
  78.9% { transform: scale(1.01) }
  81.6% { transform: scale(1.00) }
  84.2% { transform: scale(0.999) }
  86.8% { transform: scale(0.997) }
  89.5% { transform: scale(0.997) }
  92.1% { transform: scale(0.998) }
  94.7% { transform: scale(0.999) }
  97.4% { transform: scale(1.00) }
  100% { transform: scale(1.00) }
`;

export const flickerAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

export const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
